import { skyTwinCloseCameraFrame } from './245-sky-twin-framing.mjs';
import { skyCinderCameraFrame } from './246-sky-cinder-framing.mjs';
import { skyOrchidOrbitCameraFrame } from './241-sky-orchid-framing.mjs';
import { skyBrideCameraFrame } from './244-sky-bride-framing.mjs';
import { skyScrapCameraFrame } from './240-sky-scrap-framing.mjs';
import { SKY_TERRAIN, skyTerrainHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { SKY_LATERAL_LIMIT } from '../../../packages/domain/src/75-sky-rider.mjs';
import { SKY_GUARDIAN_ENTRY_SECONDS } from '../../../packages/domain/src/76-sky-campaign.mjs';
import {
  skyTraversalFrame,
  skyDragonAimControl,
  SKY_TRAVERSAL_TIMING,
} from '../../../packages/domain/src/88-sky-encounter-beat.mjs';

/** The Bride camera follows the same real inward-facing flight basis used by
 * the pilot and cannon. No independent orbit clock, roll or cosmetic rotation;
 * pause and reduced motion therefore retain the exact same physical view. */
export function skyOrbitalCameraFrame(state, aspect, aim) {
  if (!state.orbitalFlight || !state.flightFrame) return null;
  if (state.boss?.orchid) return skyOrchidOrbitCameraFrame(state, aspect, cameraPointFov);
  return skyBrideCameraFrame(state, aspect, cameraPointFov, aim);
}

/** Maximum lateral tracking error in world metres for the close shoulder view. */
export const SKY_CAMERA_TRACKING_LAG = 0.12;

/** Gameplay framing in metres/degrees. Narrow views follow lateral travel so a
 * readable1.8-metre rider stays on screen across the full steering corridor.
 * The caller owns active-time smoothing; this projection owns no gameplay state. */
export function skyCameraFrame(
  aspect,
  x = 0,
  aimY = 4.4,
  laneY = 0,
  flightCeiling = SKY_WORLD.maximumAimHeight,
) {
  const portrait = aspect < 1;
  const playerX = Math.max(-SKY_LATERAL_LIMIT, Math.min(SKY_LATERAL_LIMIT, x));
  // Follow the physical aim altitude so a close shoulder view retains the feet
  // and helmet at both corridor extremes without changing the actor's scale.
  const altitude = Math.max(
    SKY_WORLD.pilotAimHeight - SKY_TERRAIN.roadAmplitude,
    Math.min(flightCeiling, aimY - laneY),
  );
  const distance = 5.85;
  return {
    fov: portrait ? 58 : 56,
    z: SKY_WORLD.pilotZ + distance,
    y: laneY + altitude + 1.5,
    x: playerX + (portrait ? 0.48 : 1.65),
    lookX: playerX,
    lookY: laneY + altitude + 0.4,
    lookZ: -34,
  };
}

/** Keep a pursuing guardian in the close shoulder composition. This bounded
 * look correction follows authoritative world travel, including reduced motion;
 * it never moves the pilot, target or reticle independently of their geometry. */
export function skyEncounterCameraFrame(frame, state, aspect = 16 / 9) {
  const boss = state.boss;
  if (!boss) return frame;
  if (boss.mourning) return skyBrideCameraFrame(state, aspect, cameraPointFov) ?? frame;
  if (boss.orchid)
    return state.orbitalFlight
      ? skyOrbitalCameraFrame(state, aspect)
      : skyOrchidCameraFrame(frame, state, aspect);
  if (boss.twins) return skyTwinCameraFrame(frame, state, aspect);
  if (boss.scrap) return skyScrapCameraFrame(frame, state, aspect, cameraPointFov);
  if (boss.cinderheart && aspect < 1)
    return skyCinderCameraFrame(frame, state, aspect, cameraPointFov);
  const phase = Math.max(0, Math.min(1, ((boss.age ?? 0) - SKY_GUARDIAN_ENTRY_SECONDS) / 2));
  const blend = phase * phase * (3 - 2 * phase);
  return {
    ...frame,
    lookX: frame.lookX + Math.tanh((boss.x - state.x) / 4) * 0.5 * blend,
    lookY: frame.lookY + Math.tanh((boss.y - state.y) / 4) * 0.4 * blend,
  };
}

/** Frame the original garden above its central arch. The lens follows the
 * actual pilot and source bounds; no scene scaling or physical pilot offset is
 * used. A bounded lens retreat preserves narrow views and low waterfall dives. */
export function skyOrchidCameraFrame(base, state, aspect) {
  const bounds = state.boss.authoredPose.bounds;
  const frame = {
    ...base,
    fov: aspect < 1 ? 80 : 58,
    y: Math.max(base.y, state.boss.orchid.entryHeight - 2),
    lookX: state.x * 0.35 + (bounds.min[0] + bounds.max[0]) * 0.325,
    lookY: Math.max(state.y - 8, Math.min(state.y + 8, (bounds.min[1] + bounds.max[1]) / 2)),
    lookZ: (bounds.min[2] + bounds.max[2]) / 2,
  };
  const corners = [];
  for (const x of [bounds.min[0], bounds.max[0]])
    for (const y of [bounds.min[1], bounds.max[1]])
      for (const z of [bounds.min[2], bounds.max[2]]) corners.push({ x, y, z });
  const width = Math.max(...corners.map((p) => Math.abs(p.x - frame.x)));
  const depth = width / (Math.max(0.2, aspect) * Math.tan((48 * Math.PI) / 180) * 0.86);
  frame.z = Math.max(frame.z, Math.min(base.z + 30, bounds.max[2] + depth));
  // Low water passes retain the real pilot below the raised lens, rather than
  // pushing the camera back through the opaque planter's original triangles.
  frame.z = Math.max(frame.z, (state.pilotZ ?? SKY_WORLD.pilotZ) + (frame.y - state.y) * 1.6);
  for (const p of [...corners, { x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ }])
    includeCameraPoint(frame, p, aspect, 0.9);
  return frame;
}

/** Widen only when an authoritative point would leave the shot. This changes
 * projection, never the target or physical shot, and is continuous at the edge. */
function cameraPointFov(frame, point, aspect, margin) {
  const yaw = Math.atan2(frame.lookX - frame.x, frame.lookZ - frame.z);
  const pitch = Math.atan2(
    frame.lookY - frame.y,
    Math.hypot(frame.lookX - frame.x, frame.lookZ - frame.z),
  );
  const delta = { x: point.x - frame.x, y: point.y - frame.y, z: point.z - frame.z };
  const depth =
    delta.x * Math.sin(yaw) * Math.cos(pitch) +
    delta.y * Math.sin(pitch) +
    delta.z * Math.cos(yaw) * Math.cos(pitch);
  if (depth <= 0.1) return 180;
  const horizontal = -Math.cos(yaw) * delta.x + Math.sin(yaw) * delta.z;
  const vertical =
    -Math.sin(yaw) * Math.sin(pitch) * delta.x +
    Math.cos(pitch) * delta.y -
    Math.cos(yaw) * Math.sin(pitch) * delta.z;
  const required =
    (Math.atan(Math.max(Math.abs(horizontal) / aspect, Math.abs(vertical)) / (depth * margin)) *
      360) /
    Math.PI;
  return required;
}
function includeCameraPoint(frame, point, aspect, margin, weight = 1) {
  const required = cameraPointFov(frame, point, aspect, margin);
  if (required < 180) frame.fov += Math.max(0, Math.min(100, required) - frame.fov) * weight;
  return frame;
}

/** Source-aware close Twin lens; its physical objective policy belongs to245. */
export function skyTwinCameraFrame(base, state, aspect) {
  return skyTwinCloseCameraFrame(base, state, aspect, cameraPointFov);
}

/** During a physically close exposed pass the lens moves beside the pilot's
 * arm to reveal the actual face. Off-target aim widens the shot, preserving the
 * real ballistic sight instead of replacing it with a decorative crosshair.
 * Head anchors and approach weight come from the authoritative encounter. */
function wideHeadFrame(frame, state, aspect, aim, head, scale) {
  // The retreat shot keeps the cannon foregrounded on the left, leaving a
  // separate sightline to the mouth and the start of its physical flame cone.
  const mouth = state.boss.anatomy.attackSocket;
  const steering = state.boss.controlMode === 'steer';
  const retreatDepth = Math.max(1, frame.z - mouth.z);
  const pilotDepth = Math.max(0.5, frame.z - (state.pilotZ ?? SKY_WORLD.pilotZ));
  // A dodging pilot can pass to the right of the physical mouth. Increase the
  // same shoulder's parallax continuously so the flame source does not cross
  // behind the helmet during the return from a protected close view.
  const dodge = steering
    ? Math.max(0, state.x - mouth.x) / Math.max(0.8, retreatDepth / pilotDepth - 1)
    : 0;
  const envelopeDepth = Math.max(0.5, frame.z - (state.pilotZ ?? SKY_WORLD.pilotZ) - 0.7);
  // Solve lateral parallax against the same0.9m equipment envelope included
  // below. Nearer pilot geometry moves more than the distant mouth; reserve
  // a six-percent NDC gap at the widest100-degree framing before smoothing.
  const clearance = 0.06 * aspect * Math.tan((100 * Math.PI) / 360);
  const requiredShoulder =
    (0.9 * retreatDepth +
      (state.x - mouth.x) * envelopeDepth +
      clearance * envelopeDepth * retreatDepth) /
    Math.max(0.5, retreatDepth - envelopeDepth);
  const lens = {
    ...frame,
    x: Math.max(
      frame.x + (aspect < 1 ? 1.1 : 0.35) + (steering ? (aspect < 1 ? 1.35 : 1.3) : 0) + dodge,
      steering ? state.x + requiredShoulder : -Infinity,
    ),
  };
  const radius = 0.7 * scale;
  const points = [aim, { x: state.x, y: state.y - SKY_WORLD.pilotAimHeight, z: SKY_WORLD.pilotZ }];
  if (steering)
    for (const x of [-0.9, 0.9])
      for (const y of [-SKY_WORLD.pilotAimHeight, 1])
        points.push({
          x: state.x + x,
          y: state.y + y,
          z: (state.pilotZ ?? SKY_WORLD.pilotZ) + 0.7,
        });
  for (const x of [-radius, radius])
    for (const y of [-radius, radius])
      for (const z of [-radius, radius])
        points.push({ x: head.x + x, y: head.y + y, z: head.z + z });
  const yaw = points.map((p) => Math.atan2(p.x - lens.x, lens.z - p.z));
  const pitch = points.map((p) => Math.atan2(p.y - lens.y, Math.hypot(p.x - lens.x, p.z - lens.z)));
  const middleYaw = (Math.min(...yaw) + Math.max(...yaw)) / 2,
    middlePitch = (Math.min(...pitch) + Math.max(...pitch)) / 2;
  const spreadX = (Math.max(...yaw) - Math.min(...yaw)) * 0.57,
    spreadY = (Math.max(...pitch) - Math.min(...pitch)) * 0.57;
  const desired = {
    ...lens,
    fov: Math.max(
      frame.fov,
      (360 / Math.PI) * Math.max(spreadY, Math.atan(Math.tan(spreadX) / aspect)),
    ),
    lookX: lens.x + Math.sin(middleYaw) * Math.cos(middlePitch) * 20,
    lookY: lens.y + Math.sin(middlePitch) * 20,
    lookZ: lens.z - Math.cos(middleYaw) * Math.cos(middlePitch) * 20,
  };
  const weight = routeEase(((state.boss.age ?? 0) - SKY_GUARDIAN_ENTRY_SECONDS) / 1.5);
  return Object.fromEntries(
    Object.entries(desired).map(([key, value]) => [
      key,
      frame[key] + (value - frame[key]) * weight,
    ]),
  );
}

export function skyHeadCameraFrame(frame, state, aspect, aim) {
  const boss = state.boss,
    head = boss?.anatomy?.headFocus,
    mouth = boss?.anatomy?.attackSocket;
  if (!head || !mouth || !aim || state.traversal || boss.defeated) return frame;
  const scale = SKY_WORLD_ASSETS.dragon.width / 9;
  const wide = wideHeadFrame(frame, state, aspect, aim, head, scale);
  const separation = Math.hypot(aim.x - head.x, aim.y - head.y, aim.z - head.z);
  const error = Math.hypot(aim.x - head.x, aim.y - head.y);
  const weight =
    Math.max(0, Math.min(1, boss.closeHead ?? 0)) *
    (1 - routeEase((separation - 3 * scale) / scale)) *
    (1 - routeEase((error - 0.65 * scale) / (0.5 * scale)));
  if (!weight) return includeCameraPoint(wide, aim, aspect, 0.85);
  const forwardX = mouth.x - head.x,
    forwardZ = mouth.z - head.z;
  const horizontal = Math.max(0.01, Math.hypot(forwardX, forwardZ));
  const fx = forwardX / horizontal,
    fz = forwardZ / horizontal;
  const portrait = aspect < 1;
  const distance = (portrait ? 1.5 : 1.3) * scale + Math.min(2 * scale, error * 0.75);
  // A turning head must not drag the lens across the pilot's torso. Keep the
  // real shoulder behind and to its right; the cannon remains physical.
  const desired = {
    x: Math.max(head.x + fx * distance + fz * 0.35, state.x + 0.6),
    y: Math.max(head.y + 0.15 * scale, state.y + 0.25),
    z: Math.max(head.z + fz * distance - fx * 0.35, (state.pilotZ ?? SKY_WORLD.pilotZ) + 0.8),
    lookX: head.x + (aim.x - head.x) * 0.3,
    lookY: head.y + (aim.y - head.y) * 0.3,
    lookZ: head.z + (aim.z - head.z) * 0.3,
    fov: portrait ? 54 : 45,
  };
  const framed = Object.fromEntries(
    Object.entries(desired).map(([key, value]) => [key, wide[key] + (value - wide[key]) * weight]),
  );
  return includeCameraPoint(framed, aim, aspect, 0.85);
}

/** Active-time framing settles target acquisition over a short finite response.
 * A real ray can switch from skin to distant scenery in one tick; that boundary
 * must not teleport the lens. Paused redraws freeze; scrub/rewind resets. */
export function createSkyHeadView() {
  let previous = null,
    lastTime = null,
    wasActive = false,
    wasTraversal = false;
  return {
    update(frame, state, aspect, aim) {
      const target = skyHeadCameraFrame(frame, state, aspect, aim);
      const active = Boolean(
        state.boss?.anatomy?.headFocus &&
        !state.traversal &&
        !state.boss.defeated &&
        !state.presentationPose,
      );
      const elapsed = lastTime === null ? 0 : state.time - lastTime;
      if (
        !previous ||
        elapsed < 0 ||
        elapsed > 0.25 ||
        (!active && !wasActive) ||
        state.traversal ||
        wasTraversal
      )
        previous = target;
      else if (elapsed > 0) {
        // About 0.27 seconds to settle 95%: enough to read the next shot
        // without delaying the physical aim or adding motion while paused.
        const blend = 1 - Math.exp(-elapsed * 11);
        previous = Object.fromEntries(
          Object.entries(target).map(([key, value]) => [
            key,
            previous[key] + (value - previous[key]) * blend,
          ]),
        );
      }
      lastTime = state.time;
      wasActive = active;
      wasTraversal = Boolean(state.traversal);
      const result = { ...previous };
      if (active && state.boss.controlMode === 'steer')
        includeCameraPoint(result, aim, aspect, 0.85);
      return result;
    },
    reset() {
      previous = null;
      lastTime = null;
      wasActive = false;
      wasTraversal = false;
    },
  };
}

const routeEase = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

/** Sample a separate authored lens rail. Its control points are captured from
 * the launch envelope, never the current pilot heading or animated head. */
function traversalLens(route, distance, shoulder, lift) {
  const centre = route.center;
  const points = (route.pathPoints ?? route.points).map((point, index, all) => {
    if (index === 0 || index === all.length - 1)
      return { x: point.x + shoulder, y: point.y + 1.5, z: point.z + 5.85 };
    const dx = centre.x - point.x,
      dy = centre.y - point.y,
      dz = centre.z - point.z;
    const length = Math.max(0.01, Math.hypot(dx, dy, dz));
    const horizontal = Math.max(0.01, Math.hypot(dx, dz));
    return {
      x: point.x - (dx / length) * distance - (dz / horizontal) * shoulder,
      y: point.y - (dy / length) * distance + lift,
      z: point.z - (dz / length) * distance + (dx / horizontal) * shoulder,
    };
  });
  const progress = route.returning ? route.returnProgress : route.progress;
  const at = Math.max(0, Math.min(1, progress ?? 0)) * (points.length - 1);
  const index = Math.min(points.length - 2, Math.floor(at)),
    t = at - index;
  const point = (offset) => points[Math.max(0, Math.min(points.length - 1, index + offset))];
  const a = point(-1),
    b = point(0),
    c = point(1),
    d = point(2);
  return Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [
      axis,
      0.5 *
        (2 * b[axis] +
          (-a[axis] + c[axis]) * t +
          (2 * a[axis] - 5 * b[axis] + 4 * c[axis] - d[axis]) * t ** 2 +
          (-a[axis] + 3 * b[axis] - 3 * c[axis] + d[axis]) * t ** 3),
    ]),
  );
}

/** A protected flyby has an independent boss-relative camera rail. Tracking
 * can compose the actors, but neither actor's instantaneous heading moves the
 * lens. Entry and return restore the true player POV before combat resumes. */
export function skyTraversalCameraFrame(state, aspect, base, aim) {
  const route = state.traversal;
  if (!route) return base;
  // The protected return already uses the next canonical control mode. Leaving
  // the copy in 'flyby' would add the dodge shoulder only after route removal.
  const action = { ...state, traversal: null };
  if (state.boss?.controlMode === 'flyby') {
    action.boss = { ...state.boss, controlMode: 'steer', phaseAge: 0 };
    skyDragonAimControl(action.boss, 0);
  }
  if (route.reduced) return aim ? skyHeadCameraFrame(base, action, aspect, aim) : base;
  const p = route.position,
    focus = state.boss?.anatomy?.headFocus ?? route.bossFocus;
  const portrait = aspect < 1;
  const underwing = route.phase === 'underwing';
  const distance = portrait ? (underwing ? 6 : 6.6) : 5.8;
  const shoulder = portrait ? (underwing ? 1.6 : route.phase === 'overhead' ? 2.6 : 2) : 2.2;
  const lift = route.phase === 'underwing' ? 0.12 : 0.65;
  // Restore the shoulder before the pilot accelerates home; this also makes
  // the captured domain weight sufficient for a continuous skipped return.
  const weight = Math.pow(route.cameraWeight ?? skyTraversalFrame(route).cameraWeight, 4);
  const rail = traversalLens(route, distance, shoulder, lift);
  const desired = {
    ...rail,
    lookX: focus.x,
    lookY: focus.y,
    lookZ: focus.z,
    fov: portrait ? (underwing ? 72 : 64) : underwing ? 64 : 58,
  };
  desired.y = Math.max(
    desired.y,
    skyTerrainHeight(desired.x, desired.z, state.terrainTravel ?? 0) + 0.35,
  );
  const beyondLane = p.x - Math.max(-SKY_LATERAL_LIMIT, Math.min(SKY_LATERAL_LIMIT, p.x));
  const aboveLane = Math.max(0, p.y - SKY_WORLD.maximumAimHeight);
  const playable = {
    ...base,
    x: base.x + beyondLane,
    y: base.y + aboveLane,
    z: base.z + p.z - SKY_WORLD.pilotZ,
    lookX: base.lookX + beyondLane,
    lookY: base.lookY + aboveLane,
    lookZ: base.lookZ + p.z - SKY_WORLD.pilotZ,
  };
  const local = aim ? skyHeadCameraFrame(playable, action, aspect, aim) : playable;
  if (weight <= 0) return local;
  const frame = Object.fromEntries(
    Object.entries(desired).map(([key, value]) => [
      key,
      local[key] + (value - local[key]) * weight,
    ]),
  );
  // Blend bearings around the actual actor, never a chord through their body.
  const start = Math.atan2(local.x - p.x, local.z - p.z);
  const finish = Math.atan2(desired.x - p.x, desired.z - p.z);
  const turn = Math.atan2(Math.sin(finish - start), Math.cos(finish - start));
  const radius =
    Math.hypot(local.x - p.x, local.z - p.z) * (1 - weight) +
    Math.hypot(desired.x - p.x, desired.z - p.z) * weight;
  frame.x = p.x + Math.sin(start + turn * weight) * radius;
  frame.z = p.z + Math.cos(start + turn * weight) * radius;
  frame.y = Math.max(frame.y, skyTerrainHeight(frame.x, frame.z, state.terrainTravel ?? 0) + 0.35);
  const toward = { x: p.x - frame.x, y: p.y - frame.y, z: p.z - frame.z };
  const range = Math.hypot(toward.x, toward.y, toward.z);
  const baseYaw = Math.atan2(local.lookX - local.x, local.lookZ - local.z);
  const pilotYaw = Math.atan2(p.x - local.x, p.z - local.z);
  const yawOffset = Math.atan2(Math.sin(baseYaw - pilotYaw), Math.cos(baseYaw - pilotYaw));
  const basePitch = Math.atan2(
    local.lookY - local.y,
    Math.hypot(local.lookX - local.x, local.lookZ - local.z),
  );
  const pilotPitch = Math.atan2(p.y - local.y, Math.hypot(p.x - local.x, p.z - local.z));
  const halfHeight = Math.tan((frame.fov * Math.PI) / 360);
  const yaw =
    Math.atan2(toward.x, toward.z) +
    yawOffset * (1 - weight) -
    Math.atan(
      (halfHeight * aspect * 0.32) / Math.max(0.35, Math.hypot(toward.x, toward.z) / range),
    ) *
      weight;
  const pitch =
    Math.atan2(toward.y, Math.hypot(toward.x, toward.z)) +
    (basePitch - pilotPitch) * (1 - weight) +
    Math.atan(halfHeight * 0.08) * weight;
  frame.lookX = frame.x + Math.sin(yaw) * Math.cos(pitch) * range;
  frame.lookY = frame.y + Math.sin(pitch) * range;
  frame.lookZ = frame.z + Math.cos(yaw) * Math.cos(pitch) * range;
  // As the shoulder unwinds, briefly widen to keep the physical guardian in
  // view. The hero beat is unchanged; this eases away before ordinary combat.
  const hold = routeEase(
    ((route.cameraWeight ?? skyTraversalFrame(route).cameraWeight) - 0.8) / 0.1,
  );
  includeCameraPoint(frame, focus, aspect, 0.89, hold);
  if (state.boss?.anatomy?.headFocus) {
    const radius = SKY_WORLD_ASSETS.dragon.width * 0.06;
    for (const x of [-radius, radius])
      for (const y of [-radius, radius])
        for (const z of [-radius, radius])
          includeCameraPoint(
            frame,
            { x: focus.x + x, y: focus.y + y, z: focus.z + z },
            aspect,
            0.88,
            hold,
          );
  }
  return frame;
}

/** Actor orientation follows the physical flight path, independently of lens
 * position or blend. Curvature produces a small bank; the final route portion
 * returns to forward aim through the shortest turn, never a weighted full spin.
 * Apply as YXZ: world yaw, then local climb and bank for the model's -Z forward. */
export function skyTraversalPilotFrame(state) {
  const route = state.traversal;
  if (!route || route.reduced) return null;
  const duration = route.pathDuration ?? route.duration;
  const prepare = route.prepareDuration ?? 0;
  const tangentAt = (progress) =>
    skyTraversalFrame({
      ...route,
      returning: false,
      reduced: false,
      points: route.pathPoints ?? route.points,
      duration,
      age: prepare + Math.max(0, Math.min(1, progress)) * (duration - prepare),
    }).tangent;
  const heading = (t) => Math.atan2(-t.x, -t.z);
  const pitchAt = (t) => Math.max(-0.6, Math.min(0.6, Math.atan2(t.y, Math.hypot(t.x, t.z))));
  const poseAt = (progress, tangent) => {
    const sampled = Math.min(progress, 0.8);
    const direction = progress > 0.8 ? tangentAt(0.8) : tangent;
    const before = tangentAt(sampled - 0.0025),
      after = tangentAt(sampled + 0.0025);
    const turn = Math.atan2(
      Math.sin(heading(after) - heading(before)),
      Math.cos(heading(after) - heading(before)),
    );
    const bank = Math.max(-0.22, Math.min(0.22, (-turn / (0.005 * (duration - prepare))) * 0.12));
    const weight = routeEase(progress / 0.08) * (1 - routeEase((progress - 0.8) / 0.2));
    return {
      yaw: heading(direction) * weight,
      pitch: -0.08 + (pitchAt(direction) + 0.08) * weight,
      roll: bank * weight,
    };
  };
  if (route.returning) {
    const pose = poseAt(route.returnProgress ?? 0, route.returnTangent ?? route.tangent);
    const blend = 1 - routeEase(route.age / SKY_TRAVERSAL_TIMING.returnDuration);
    return {
      yaw: pose.yaw * blend,
      pitch: -0.08 + (pose.pitch + 0.08) * blend,
      roll: pose.roll * blend,
    };
  }
  return poseAt(route.progress ?? 0, route.tangent);
}

/** Bounded player-responsive parallax in metres. Simulation time drives the
 * exponential damping: repeated paused draws do not move, rewind resets, and
 * reduced motion retains the essential shoulder framing without extra drift. */
export function createSkyCameraParallax({ reduced = () => false } = {}) {
  let lastTime = null;
  let horizontal = 0;
  let vertical = 0;
  function reset() {
    lastTime = null;
    horizontal = vertical = 0;
  }
  function update(state, aspect) {
    const time = Number.isFinite(state.time) ? state.time : 0;
    const x = Number.isFinite(state.x) ? state.x : 0;
    const y = Number.isFinite(state.y) ? state.y : 4.4;
    const laneY =
      state.journeyAltitude &&
      state.journeyAltitude.sector === state.sector &&
      Number.isFinite(state.journeyAltitude.originY)
        ? state.journeyAltitude.originY
        : 0;
    if (lastTime !== null && time < lastTime) reset();
    const dt = lastTime === null ? 0 : Math.max(0, time - lastTime);
    if (reduced()) horizontal = vertical = 0;
    else if (dt > 0) {
      const response = 1 - Math.exp(-dt * 5);
      horizontal += (Math.max(-1, Math.min(1, x / SKY_LATERAL_LIMIT)) - horizontal) * response;
      vertical += (Math.max(-1, Math.min(1, (y - laneY - 4.4) / 4.6)) - vertical) * response;
    }
    lastTime = time;
    // Authored encounters allow real climbs above the approach's nine-metre
    // lane. Follow that same physical envelope so the pilot stays in frame.
    const ceiling =
      state.boss?.authoredRig && Number.isFinite(state.boss.authoredFlightCeiling)
        ? Math.max(SKY_WORLD.maximumAimHeight, state.boss.authoredFlightCeiling - laneY)
        : Number.isInteger(state.sector) &&
            state.twinLesson?.sector === state.sector &&
            Number.isFinite(state.twinLesson.flightCeiling)
          ? Math.max(SKY_WORLD.maximumAimHeight, state.twinLesson.flightCeiling)
          : SKY_WORLD.maximumAimHeight;
    const frame = skyCameraFrame(aspect, x, y, laneY, ceiling);
    frame.x += horizontal * (aspect < 1 ? 0.06 : 0.22);
    frame.y += vertical * 0.35;
    frame.lookX += horizontal * 0.65;
    frame.lookY += vertical * 0.5;
    return frame;
  }
  return { update, reset };
}

/** Presentation-only inward-facing pose, measured from the camera's horizontal
 * bearing in radians. The camera keeps its shoulder; only the body flips. Centre
 * hysteresis avoids jitter, and a finite transition cannot stall in a back view.
 * update consumes active simulation seconds; pause freezes, rewind resets. */
export function createSkyViewOrientation({ reduced = () => false } = {}) {
  let initialized = false;
  let lastTime = 0;
  let side = 1;
  let from = 1;
  let opening = 1;
  let age = 0.18;
  let result = { viewYaw: 0.58, viewYawResolved: true, viewFlipping: false };
  function reset() {
    initialized = false;
  }
  function update(state, cameraBearing = 0) {
    const time = Number.isFinite(state.time) ? state.time : 0;
    const x = Number.isFinite(state.x) ? state.x : 0;
    const requestedSide = x > 0.75 ? -1 : x < -0.75 ? 1 : side;
    const elapsed = time - lastTime;
    if (!initialized || elapsed < 0) {
      side = x > 0.75 ? -1 : 1;
      opening = from = side;
      age = 0.18;
      initialized = true;
    } else {
      if (elapsed <= 0) return result;
      // Complete a committed flip before accepting its opposite, so rapid
      // centre zigzags cannot keep the body oscillating around a straight back.
      if (age >= 0.18 && requestedSide !== side) {
        from = opening;
        side = requestedSide;
        age = 0;
      }
      if (reduced()) {
        opening = from = side;
        age = 0.18;
      } else {
        age = Math.min(0.18, age + Math.min(0.1, elapsed));
        const progress = age / 0.18;
        const eased = progress * progress * (3 - 2 * progress);
        opening = from + (side - from) * eased;
      }
    }
    lastTime = time;
    const bearing = Number.isFinite(cameraBearing)
      ? Math.max(-0.27, Math.min(0.27, cameraBearing))
      : 0;
    result = {
      viewYaw: opening * 0.58 - bearing,
      viewYawResolved: true,
      viewFlipping: age < 0.18,
    };
    return result;
  }
  return { update, reset };
}
